import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { ClientSummary } from '@/api/types';
import {
  Avatar,
  Button,
  Card,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Tag,
  Table,
  TextInput,
} from '@/components/ui';
import { formatDate, humanise } from '@/lib/format';
import { avatarColor, initials } from '@/lib/identity';

type DuplicateCandidate = { id: string; full_name: string; matched_on: string };

/** ADM-01. Clients are the way into everything else. */
export function ClientsPage() {
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<{ clients: ClientSummary[] }>('/admin/clients');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const clients = useMemo(() => {
    const all = data?.clients ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((client) =>
      `${client.full_name} ${client.preferred_name ?? ''}`.toLowerCase().includes(needle),
    );
  }, [data, search]);

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;

  return (
    <Page>
      <PageHead
        title="Clients"
        subtitle="Care plans and activity by client"
        actions={<Button onClick={() => setCreating(true)}>Add a client</Button>}
      />

      <div className="stack">
        <Card>
          <Field label="Search clients">
            {(props) => (
              <TextInput
                {...props}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name"
              />
            )}
          </Field>
        </Card>

        <Table
          columns={['Client', 'Care plan', 'Consent', 'Latest visit', 'Feedback']}
          empty={search ? 'No client matches that search.' : 'No client records yet.'}
        >
          {clients.map((client) => (
            <tr key={client.id} data-clickable="true" onClick={() => navigate(`/clients/${client.id}`)}>
              <td>
                <div className="row">
                  <Avatar person={{ name: client.full_name, initials: initials(client.full_name), color: avatarColor(client.id) }} />
                  <div>
                    <div className="strong">{client.preferred_name ?? client.full_name}</div>
                    <div className="meta">{humanise(client.service_status)}</div>
                  </div>
                </div>
              </td>
              <td>
                {client.plan_awaiting_publication ? (
                  <Tag label="Update awaiting publication" tone="attention" />
                ) : client.has_current_plan ? (
                  <span className="meta">
                    Effective {formatDate(client.plan_effective_date)}
                  </span>
                ) : (
                  <Tag label="No published plan" tone="urgent" />
                )}
              </td>
              <td>
                {client.consent_status === 'granted' ? (
                  <Tag label="Granted" tone="positive" />
                ) : (
                  <Tag label={humanise(client.consent_status)} tone="attention" />
                )}
              </td>
              <td className="meta">
                {client.last_visit_at ? formatDate(client.last_visit_at) : 'None completed'}
              </td>
              <td>
                {client.open_feedback_count > 0 ? (
                  <Tag label={`${client.open_feedback_count} open`} tone="attention" />
                ) : (
                  <span className="meta">—</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </div>

      {creating ? (
        <NewClientModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(`/clients/${id}`);
          }}
        />
      ) : null}
    </Page>
  );
}

/**
 * ADM-01 requires duplicates to be *flagged*, not blocked and not auto-merged.
 * The API returns candidates with a 409; this form shows them and lets a human
 * decide, which is the only party that can.
 */
function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [fullName, setFullName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dob, setDob] = useState('');
  const [phone, setPhone] = useState('');
  const [consentReference, setConsentReference] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(acknowledgeDuplicates: boolean) {
    setBusy(true);
    setProblem(null);
    try {
      const response = await api.post<{ client: { id: string } }>('/admin/clients', {
        full_name: fullName.trim(),
        preferred_name: preferredName.trim() || null,
        date_of_birth: dob || null,
        phone: phone.trim() || null,
        consent_status: consentReference.trim() ? 'granted' : 'not_recorded',
        consent_reference: consentReference.trim() || null,
        acknowledge_duplicates: acknowledgeDuplicates,
      });
      onCreated(response.client.id);
    } catch (caught) {
      const error = caught as ApiError;
      if (error.status === 409 && error.details.duplicate_candidates) {
        setDuplicates(error.details.duplicate_candidates as DuplicateCandidate[]);
      } else {
        setProblem(error.problems.join('\n') || error.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Add a client" onClose={onClose}>
      <div className="stack">
        {problem ? (
          <Notice tone="urgent" title="Could not save">
            {problem}
          </Notice>
        ) : null}

        {duplicates ? (
          <Notice tone="attention" title="This may duplicate an existing record">
            {duplicates
              .map((candidate) => `• ${candidate.full_name} — same ${humanise(candidate.matched_on).toLowerCase()}`)
              .join('\n')}
            {'\n\nCheck before creating a second record for the same person.'}
          </Notice>
        ) : null}

        <Field label="Full name">
          {(props) => (
            <TextInput
              {...props}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              autoFocus
            />
          )}
        </Field>

        <Field label="Preferred name" hint="What this person likes to be called">
          {(props) => (
            <TextInput
              {...props}
              value={preferredName}
              onChange={(event) => setPreferredName(event.target.value)}
            />
          )}
        </Field>

        <div className="grid-2">
          <Field label="Date of birth">
            {(props) => (
              <TextInput {...props} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            )}
          </Field>
          <Field label="Phone">
            {(props) => (
              <TextInput
                {...props}
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            )}
          </Field>
        </div>

        <Field
          label="Consent or legal authority reference"
          hint="Family access cannot be granted until this is recorded."
        >
          {(props) => (
            <TextInput
              {...props}
              value={consentReference}
              onChange={(event) => setConsentReference(event.target.value)}
              placeholder="e.g. signed service agreement reference"
            />
          )}
        </Field>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => submit(duplicates !== null)} busy={busy} disabled={!fullName.trim()}>
            {duplicates ? 'Create anyway' : 'Create client'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
